import { Avatar, Card, Carousel } from 'antd';
import Title from 'antd/lib/typography/Title';
import { Maybe } from 'graphql/jsutils/Maybe';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { extractTranslation, formatPortalFacilityPath } from '../../helpers/formatters';
import { buildImageDownloadUrl, renderTextWithUrls } from '../../helpers/utils';
import {
  Patientportal_PortalFacilityMsg,
  useGetPortalFacilityPathsQuery,
} from '../../src/generated/graphql';
import styles from './facility-selection.module.scss';

interface FacilityHeaderProps {
  facility: Maybe<Patientportal_PortalFacilityMsg> | undefined;
  clinicId: Maybe<string> | undefined;
  simplified?: boolean;
}

export const FacilityHeader: FC<FacilityHeaderProps> = ({
  facility,
  clinicId,
  simplified = false,
}) => {
  const { formatMessage } = useIntl();
  const router = useRouter();
  const locale = router.locale;

  const [showFullText, setShowFullText] = useState<boolean>(false);

  const name = facility?.name;
  const longDescription = extractTranslation(facility?.longDescription, locale);

  const logoId = facility?.logoImageId;

  const { data: portalFacilityPathsData } = useGetPortalFacilityPathsQuery({
    variables: {
      portalFacilityIds: facility?.id ? [facility.id] : [],
    },
  });

  const portalFacilityPath =
    portalFacilityPathsData?.getPortalFacilityPaths?.paths?.[0]?.portalFacilities?.slice(0, -1);
  const headerImageIds = facility?.headerImageIds;

  return (
    <div style={{ marginBottom: 8 }}>
      {!simplified && headerImageIds && headerImageIds?.length > 0 && (
        <Carousel autoplay>
          {headerImageIds?.map((imageId) => (
            <div id={'image-wrapper'} key={imageId} className={styles.facilityImageWrapper}>
              <Image
                alt={facility!.name!}
                layout={'fill'}
                objectFit={'cover'}
                className={styles.facilityImage}
                src={buildImageDownloadUrl(imageId!)}
              />
            </div>
          ))}
        </Carousel>
      )}
      <Card>
        <div className={styles.titleWrapper}>
          {logoId && (
            <div className={styles.logoWrapper}>
              <Avatar
                size={45}
                className={styles.logo}
                src={buildImageDownloadUrl(logoId)}
                alt={`Logo ${name}`}
              />
            </div>
          )}
          <div className={styles.title}>
            <Title level={2}>{name}</Title>
            <span className={styles.hierarchy}>
              {formatPortalFacilityPath(portalFacilityPath, true)}
            </span>
          </div>
        </div>
        {!simplified && (
          <div
            className={`${
              showFullText ? styles.clinicDescriptionLongUnlimited : styles.clinicDescriptionLong
            } ant-list-item-meta-description description`}
            onClick={() => setShowFullText(!showFullText)}
          >
            {renderTextWithUrls(longDescription) ||
              formatMessage({ id: 'facility-selection.no-description' })}
          </div>
        )}
      </Card>
    </div>
  );
};
